import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

/* ── Helpers ─────────────────────────────────────────────────────────── */
function fmtQty(n) {
  const v = parseFloat(n ?? 0);
  if (isNaN(v)) return '0';
  return v % 1 === 0 ? String(v) : v.toFixed(3).replace(/\.?0+$/, '');
}

function fmtDt(str) {
  if (!str) return '-';
  const d = new Date(str);
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth()+1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Usado solo con fechas de filtro puras 'YYYY-MM-DD' (fromDate/toDate) — un
// string sin hora se interpreta como medianoche UTC; pasarlo por Date +
// getters locales lo correría un día atrás en Perú. Reformateo directo.
function fmtDate(str) {
  if (!str) return '-';
  const m = String(str).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(str).slice(0, 10);
}

const TIPO_LABEL = {
  ingreso: 'INGRESO', salida: 'SALIDA', conversion: 'CONVERSIÓN',
  sale: 'VENTA', purchase: 'COMPRA', adjustment: 'AJUSTE',
  return: 'DEVOL.', transferencia: 'TRANSFER.'
};

function buildRows(data) {
  return data.map((r, i) => {
    const isIn = ['ingreso','purchase'].includes(r.movement_type) || ['TRANSFERENCIA ENTRADA','CONVERSION ENTRADA'].includes(r.causa);
    return {
      num:       i + 1,
      fecha:     fmtDt(r.moved_at),
      almacen:   r.warehouse_name || '-',
      producto:  r.product_name   || '-',
      sku:       r.sku || '-',
      tipo:      TIPO_LABEL[r.movement_type] || r.movement_type?.toUpperCase() || '-',
      causa:     r.causa        || '-',
      proveedor: r.provider_name || '-',
      saldoAnt:  fmtQty(r.stock_before),
      mov:       (isIn ? '+' : '-') + fmtQty(r.qty_moved),
      saldoAct:  fmtQty(r.stock_after),
      isIn,
    };
  });
}

function buildFilterDesc(filters) {
  const parts = [];
  if (filters.fromDate) parts.push(`Desde: ${fmtDate(filters.fromDate)}`);
  if (filters.toDate)   parts.push(`Hasta: ${fmtDate(filters.toDate)}`);
  if (filters.warehouse) parts.push(`Almacén: ${filters.warehouse}`);
  if (filters.type)      parts.push(`Tipo: ${TIPO_LABEL[filters.type] || filters.type}`);
  if (filters.product)   parts.push(`Producto: ${filters.product}`);
  return parts.length ? parts.join('  |  ') : 'Sin filtros adicionales';
}

/* ──────────────────────────────────────────────────────────────────────
   EXPORTAR PDF
─────────────────────────────────────────────────────────────────────── */
export function exportPDF({ empresa, data, filters, totals, usuario }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const navy   = [13, 27, 46];
  const accent = [14, 165, 233];
  const green  = [22, 163, 74];
  const red    = [220, 38, 38];
  const gray   = [100, 116, 139];

  // ── ENCABEZADO EMPRESA ──────────────────────────────────────────────
  doc.setFillColor(...navy);
  doc.rect(0, 0, W, 22, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text(empresa.name || 'PIVASAPOLLOS', 12, 10);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(180, 210, 230);
  const rucLine = `RUC: ${empresa.ruc || '20100000001'}  |  ${empresa.address || 'AV. BENAVIDES NRO 6600-6606, MIRAFLORES - LIMA'}`;
  doc.text(rucLine, 12, 16);

  const now = new Date();
  const nowStr = fmtDt(now.toISOString());
  doc.setTextColor(180, 210, 230);
  doc.text(`Generado: ${nowStr}  |  Usuario: ${usuario || 'sistema'}`, W - 12, 16, { align: 'right' });

  // ── TÍTULO DEL REPORTE ──────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...navy);
  doc.text('REPORTE DE MOVIMIENTOS DE INVENTARIO', W / 2, 30, { align: 'center' });

  // Línea decorativa
  doc.setDrawColor(...accent);
  doc.setLineWidth(0.5);
  doc.line(12, 33, W - 12, 33);

  // ── FILTROS ─────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...gray);
  doc.text(buildFilterDesc(filters), 12, 38);

  // ── RESUMEN ─────────────────────────────────────────────────────────
  const sumY = 43;
  const boxW = 52;
  const boxes = [
    { label: 'Total Movimientos', value: String(totals.count), color: navy },
    { label: 'Total Entradas',    value: `+${fmtQty(totals.totalIn)}`, color: green },
    { label: 'Total Salidas',     value: `-${fmtQty(totals.totalOut)}`, color: red },
  ];
  boxes.forEach((b, i) => {
    const x = 12 + i * (boxW + 4);
    doc.setFillColor(b.color[0], b.color[1], b.color[2]);
    doc.roundedRect(x, sumY, boxW, 10, 2, 2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.text(b.label, x + boxW / 2, sumY + 3.5, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(b.value, x + boxW / 2, sumY + 8, { align: 'center' });
  });

  // ── TABLA ───────────────────────────────────────────────────────────
  const rows  = buildRows(data);
  const cols  = ['#','Fecha','Almacén','Producto','SKU','Tipo','Causa','Proveedor','Saldo Ant.','Movimiento','Saldo Act.'];
  const body  = rows.map(r => [r.num, r.fecha, r.almacen, r.producto, r.sku, r.tipo, r.causa, r.proveedor, r.saldoAnt, r.mov, r.saldoAct]);

  autoTable(doc, {
    startY: sumY + 15,
    head: [cols],
    body,
    theme: 'grid',
    styles: {
      fontSize: 7.5,
      cellPadding: { top: 2.5, right: 3, bottom: 2.5, left: 3 },
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: navy,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 7.5,
    },
    columnStyles: {
      0:  { halign: 'center', cellWidth: 8  },  // #
      1:  { cellWidth: 30 },                     // Fecha
      2:  { cellWidth: 28 },                     // Almacén
      3:  { cellWidth: 38, fontStyle: 'bold' },  // Producto
      4:  { cellWidth: 16, halign: 'center' },   // SKU
      5:  { cellWidth: 18, halign: 'center' },   // Tipo
      6:  { cellWidth: 24 },                     // Causa
      7:  { cellWidth: 28 },                     // Proveedor
      8:  { cellWidth: 20, halign: 'right' },    // Saldo Ant.
      9:  { cellWidth: 22, halign: 'right', fontStyle: 'bold' },  // Movimiento
      10: { cellWidth: 20, halign: 'right', fontStyle: 'bold' },  // Saldo Act.
    },
    didParseCell(data) {
      if (data.section === 'body') {
        const row = rows[data.row.index];
        if (row?.isIn) {
          data.cell.styles.fillColor = [240, 253, 244];  // verde muy claro
        } else {
          data.cell.styles.fillColor = [255, 247, 247];  // rojo muy claro
        }
        // Colorear columna Movimiento
        if (data.column.index === 9) {
          data.cell.styles.textColor = row?.isIn ? [22, 163, 74] : [220, 38, 38];
        }
      }
    },
    didDrawPage(data) {
      // Footer con número de página
      const pageCount = doc.internal.getNumberOfPages();
      const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...gray);
      doc.text(
        `Página ${currentPage} de ${pageCount}  —  PIVASAPOLLOS  —  ${nowStr}`,
        W / 2,
        doc.internal.pageSize.getHeight() - 5,
        { align: 'center' }
      );
    },
  });

  const filename = `Movimientos_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
  doc.save(filename);
}

/* ──────────────────────────────────────────────────────────────────────
   EXPORTAR EXCEL
─────────────────────────────────────────────────────────────────────── */
export function exportExcel({ empresa, data, filters, totals, usuario }) {
  const wb = XLSX.utils.book_new();
  const ws_data = [];

  // ── Estilos base (XLSX soporta estilos básicos en .xlsx) ────────────
  const S = {
    title:    { font: { bold: true, sz: 16, color: { rgb: '0D1B2E' } }, alignment: { horizontal: 'center' } },
    subtitle: { font: { bold: true, sz: 11, color: { rgb: '0D1B2E' } }, alignment: { horizontal: 'center' } },
    label:    { font: { bold: true, sz: 9,  color: { rgb: '475569' } } },
    value:    { font: { sz: 9, color: { rgb: '1e293b' } } },
    hdr:      { font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '0D1B2E' } }, alignment: { horizontal: 'center' }, border: { bottom: { style: 'thin', color: { rgb: '0EA5E9' } } } },
    numR:     { font: { sz: 9 }, alignment: { horizontal: 'right' } },
    boldR:    { font: { bold: true, sz: 9 }, alignment: { horizontal: 'right' } },
    numC:     { font: { sz: 9 }, alignment: { horizontal: 'center' } },
    inRow:    { font: { sz: 9 }, fill: { fgColor: { rgb: 'F0FDF4' } } },
    outRow:   { font: { sz: 9 }, fill: { fgColor: { rgb: 'FFF7F7' } } },
    inBold:   { font: { bold: true, sz: 9, color: { rgb: '16A34A' } }, fill: { fgColor: { rgb: 'F0FDF4' } }, alignment: { horizontal: 'right' } },
    outBold:  { font: { bold: true, sz: 9, color: { rgb: 'DC2626' } }, fill: { fgColor: { rgb: 'FFF7F7' } }, alignment: { horizontal: 'right' } },
    sumIn:    { font: { bold: true, sz: 10, color: { rgb: '16A34A' } } },
    sumOut:   { font: { bold: true, sz: 10, color: { rgb: 'DC2626' } } },
    sumTotal: { font: { bold: true, sz: 10, color: { rgb: '0D1B2E' } } },
  };

  const cell = (v, s) => ({ v, t: typeof v === 'number' ? 'n' : 's', s });

  // ── Filas de encabezado ─────────────────────────────────────────────
  ws_data.push([cell(empresa.name || 'PIVASAPOLLOS', S.title)]);
  ws_data.push([cell(`RUC: ${empresa.ruc || '20100000001'}  |  ${empresa.address || 'AV. BENAVIDES NRO 6600-6606, MIRAFLORES - LIMA'}`, S.value)]);
  ws_data.push([]);
  ws_data.push([cell('REPORTE DE MOVIMIENTOS DE INVENTARIO', S.subtitle)]);
  ws_data.push([cell(buildFilterDesc(filters), { font: { sz: 8.5, color: { rgb: '64748B' } } })]);
  ws_data.push([cell(`Generado: ${fmtDt(new Date().toISOString())}  |  Usuario: ${usuario || 'sistema'}`, { font: { sz: 8, italic: true, color: { rgb: '94A3B8' } } })]);
  ws_data.push([]);

  // ── Resumen ─────────────────────────────────────────────────────────
  ws_data.push([
    cell('Total Movimientos:', S.label),
    cell(totals.count, S.sumTotal),
    cell(''),
    cell('Total Entradas:', S.label),
    cell(`+${fmtQty(totals.totalIn)}`, S.sumIn),
    cell(''),
    cell('Total Salidas:', S.label),
    cell(`-${fmtQty(totals.totalOut)}`, S.sumOut),
  ]);
  ws_data.push([]);

  // ── Encabezados de tabla ─────────────────────────────────────────────
  ws_data.push([
    cell('#',           S.hdr),
    cell('Fecha',       S.hdr),
    cell('Almacén',     S.hdr),
    cell('Producto',    S.hdr),
    cell('SKU',         S.hdr),
    cell('Tipo',        S.hdr),
    cell('Causa',       S.hdr),
    cell('Proveedor',   S.hdr),
    cell('Saldo Ant.',  S.hdr),
    cell('Movimiento',  S.hdr),
    cell('Saldo Act.',  S.hdr),
  ]);

  // ── Datos ────────────────────────────────────────────────────────────
  const rows = buildRows(data);
  rows.forEach(r => {
    const base = r.isIn ? S.inRow  : S.outRow;
    const mov  = r.isIn ? S.inBold : S.outBold;
    const numStyle = { ...S.numR, fill: base.fill };
    ws_data.push([
      cell(r.num,      { ...S.numC, fill: base.fill }),
      cell(r.fecha,    base),
      cell(r.almacen,  base),
      cell(r.producto, { font: { bold: true, sz: 9 }, fill: base.fill }),
      cell(r.sku,      { ...S.numC, fill: base.fill }),
      cell(r.tipo,     { ...S.numC, fill: base.fill }),
      cell(r.causa,    base),
      cell(r.proveedor,base),
      cell(r.saldoAnt, numStyle),
      cell(r.mov,      mov),
      cell(r.saldoAct, { ...numStyle, font: { bold: true, sz: 9 } }),
    ]);
  });

  // ── Crear la hoja ────────────────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet(ws_data, { skipHeader: true });

  // Anchos de columna
  ws['!cols'] = [
    { wpx: 35  },  // #
    { wpx: 125 },  // Fecha
    { wpx: 120 },  // Almacén
    { wpx: 160 },  // Producto
    { wpx: 65  },  // SKU
    { wpx: 75  },  // Tipo
    { wpx: 120 },  // Causa
    { wpx: 130 },  // Proveedor
    { wpx: 80  },  // Saldo Ant.
    { wpx: 85  },  // Movimiento
    { wpx: 80  },  // Saldo Act.
  ];

  // Combinar celdas del encabezado (filas 1-6 → columnas A a K)
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 10 } },  // Nombre empresa
    { s: { r: 1, c: 0 }, e: { r: 1, c: 10 } },  // RUC/dirección
    { s: { r: 3, c: 0 }, e: { r: 3, c: 10 } },  // Título
    { s: { r: 4, c: 0 }, e: { r: 4, c: 10 } },  // Filtros
    { s: { r: 5, c: 0 }, e: { r: 5, c: 10 } },  // Generado
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Movimientos');

  const fnDate = new Date();
  const filename = `Movimientos_${fnDate.getFullYear()}-${String(fnDate.getMonth() + 1).padStart(2, '0')}-${String(fnDate.getDate()).padStart(2, '0')}.xlsx`;
  XLSX.writeFile(wb, filename);
}
